/**
 * Facturación por médico colaborador (2026-08-25, dirección).
 *
 * Entrada: tarjetas de médicos con lo pendiente. Al pinchar uno: su actividad
 * (tratamientos y cirugías) con los números que el sistema calcula solo.
 *  - Tratamiento: el médico rellena tratamiento + producto + cantidad
 *    (+ descuento opcional); PVP y coste salen de los catálogos (Excel de la doctora de estética).
 *  - Cirugía: facturación mete precio + anestesia + instrumentista; los
 *    fungibles fijos y el reparto 50/50 los pone el sistema (Excel de otro cirujano).
 *
 * «Tarifario» (gestión, 7/10/2026): tratamientos, productos y cirugías con
 * sus importes, en components/colaboradores/TarifarioColaboradoresDialog.
 * Se abre también con ?tarifario=1 (el enlace del catálogo de precios) o con
 * la pestaña que se quiera: ?tarifario=cirugias (el de su sección de
 * cirugías), ?tarifario=productos.
 *
 * Los catálogos llevan su estado (revisión del 7/10/2026): mientras cargan,
 * el tarifario dice «Cargando…»; si el GET falla, lo dice con «Reintentar»
 * (nunca «Aún no hay…», que se leía como un tarifario vacío). Y si tras
 * guardar falla la recarga, también se avisa.
 *
 * «Porcentajes y fungibles» (7/10/2026) lleva, debajo del reparto general,
 * las «Excepciones por médico» (components/colaboradores/ExcepcionesReparto):
 * un médico que cobra otro % en un tipo de acto y, si se quiere, en un lugar,
 * desde una fecha. En la lista de actos y en las tarjetas de los médicos se
 * ve el % de los que llevan excepción («45 % (excepción)»), y el lugar de
 * cada cirugía.
 *
 * LISTA DE COLABORADORES, PORCENTAJES Y LIQUIDACIÓN (4/11/2026, administración):
 *  · La entrada enseña TODOS los colaboradores de la lista (aunque aún no
 *    tengan actos), con su % fijo, sus excepciones por servicio y lo
 *    pendiente; y, marcados, los médicos con actos que no están en la lista
 *    (para no esconder dinero) y los dados de baja a los que aún se les debe.
 *    «Añadir colaborador» y «Quitar» (baja blanda, con confirmación):
 *    components/colaboradores/ListaColaboradoresDialogos.
 *  · Al abrir uno: «Porcentajes» (% fijo y excepciones por servicio, solo
 *    gestión; components/colaboradores/PorcentajesColaborador) y
 *    «Liquidación» por meses con «Exportar a Excel» (gestión y el propio
 *    médico; components/colaboradores/LiquidacionColaborador).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, ChevronRight, Loader2, Plus, Settings2, Stethoscope, Syringe, Tags, Trash2, UserMinus, UserPlus,
} from 'lucide-react';
import { api, mensajeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { leerImporte, textoPorcentaje } from '../lib/importeEuros';
import ColabActividadDialog from '../components/ColabActividadDialog';
import TarifarioColaboradoresDialog, { CATALOGOS_TARIFARIO } from '../components/colaboradores/TarifarioColaboradoresDialog';
import ExcepcionesReparto from '../components/colaboradores/ExcepcionesReparto';
import { AnadirColaboradorDialog, QuitarColaboradorDialog } from '../components/colaboradores/ListaColaboradoresDialogos';
import PorcentajesColaborador from '../components/colaboradores/PorcentajesColaborador';
import LiquidacionColaborador from '../components/colaboradores/LiquidacionColaborador';
import { PageHeader } from '../components/Common';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');

/** Las tarjetas de los médicos: cuántos actos llevan un % de excepción. */
function textoExcepciones(m) {
  const n = m?.excepciones || 0;
  if (!n) return '';
  const pcts = (m.pcts_excepcion || []).map(textoPorcentaje);
  const actos = n === 1 ? '1 acto' : `${n} actos`;
  return pcts.length === 1 ? `${actos} al ${pcts[0]} (excepción)` : `${actos} con excepción (${pcts.join(', ')})`;
}

/** La línea del reparto de una tarjeta: su % fijo y sus excepciones por servicio. */
function textoReparto(m) {
  const partes = [m.pct_fijo === null || m.pct_fijo === undefined ? '% general' : `${textoPorcentaje(m.pct_fijo)} fijo`];
  const n = m.excepciones_servicio || 0;
  if (n) partes.push(n === 1 ? '1 excepción por servicio' : `${n} excepciones por servicio`);
  return partes.join(' · ');
}

/** Junto al % de un acto: de dónde sale si no es el general. */
function etiquetaPct(a) {
  const origen = a.origen_pct || (a.regla_reparto_id ? 'regla' : 'general');
  if (origen === 'servicio') return 'excepción · servicio';
  if (origen === 'regla') return 'excepción';
  if (origen === 'fijo') return '% fijo';
  return '';
}

const ROLES_GESTION = ['admin', 'direccion', 'administrativo'];
const PESTANAS_TARIFARIO = Object.keys(CATALOGOS_TARIFARIO);

export default function ColaboradoresPage() {
  const { user } = useAuth();
  const esGestion = ROLES_GESTION.includes((user?.role || '').toLowerCase());
  const [resumen, setResumen] = useState(null);
  const [medico, setMedico] = useState(null);        // {medico_id, medico_nombre}
  const [detalle, setDetalle] = useState(null);      // {items, totales}
  const [catalogos, setCatalogos] = useState(null);
  // 'cargando' | 'listo' | 'error' (el último GET de los catálogos).
  const [estadoCatalogos, setEstadoCatalogos] = useState('cargando');
  const turnoCatalogos = useRef(0);
  const [medicos, setMedicos] = useState([]);
  const [dialogo, setDialogo] = useState(null);      // 'tratamiento' | 'cirugia' | 'config' | 'tarifario'
  // Añadir a la lista ({inicial: medico_id o ''}) y quitar de ella (la tarjeta).
  const [anadiendo, setAnadiendo] = useState(null);
  const [quitando, setQuitando] = useState(null);
  const [params, setParams] = useSearchParams();

  const cargarResumen = useCallback(() => {
    if (!esGestion) return;
    api.get('/api/colaboradores/resumen')
      .then((r) => setResumen(r.data?.medicos || []))
      .catch(() => setResumen([]));
  }, [esGestion]);

  const cargarDetalle = useCallback((m) => {
    const params = m?.medico_id ? { medico_id: m.medico_id } : {};
    api.get('/api/colaboradores/actividades', { params })
      .then((r) => setDetalle(r.data))
      .catch(() => setDetalle({ items: [], totales: {} }));
  }, []);

  // Los catálogos (tarifario y porcentajes). Se vuelven a pedir tras cada
  // cambio del tarifario: así los desplegables de los actos usan ya lo nuevo.
  // Devuelve si se han podido cargar. Si falla una recarga, lo que ya se veía
  // se queda (con el aviso); solo cuenta la última petición.
  const cargarCatalogos = useCallback(() => {
    turnoCatalogos.current += 1;
    const mio = turnoCatalogos.current;
    setEstadoCatalogos('cargando');
    return api.get('/api/colaboradores/catalogos')
      .then((r) => {
        if (turnoCatalogos.current !== mio) return true;
        setCatalogos(r.data);
        setEstadoCatalogos('listo');
        return true;
      })
      .catch(() => {
        if (turnoCatalogos.current === mio) setEstadoCatalogos('error');
        return false;
      });
  }, []);

  useEffect(() => {
    cargarCatalogos();
    if (esGestion) {
      cargarResumen();
      api.get('/api/colaboradores/medicos-posibles').then((r) => setMedicos(r.data?.medicos || [])).catch(() => {});
    } else {
      // Un médico entra directo a lo suyo
      const yo = { medico_id: user?.id, medico_nombre: user?.name };
      setMedico(yo); cargarDetalle(yo);
    }
  }, [esGestion, cargarCatalogos, cargarResumen, cargarDetalle, user]);

  // «Editar en Colaboradores» desde el catálogo de precios llega con
  // ?tarifario=1 (o con la pestaña: ?tarifario=cirugias): se abre el
  // tarifario directamente (solo gestión). Mientras llegan los catálogos, el
  // tarifario dice «Cargando…»: no enseña nada como vacío.
  const pestanaPedida = params.get('tarifario');
  const pideTarifario = pestanaPedida !== null;
  const pestanaInicial = PESTANAS_TARIFARIO.includes(pestanaPedida) ? pestanaPedida : 'tratamientos';
  useEffect(() => {
    if (esGestion && pideTarifario) setDialogo('tarifario');
  }, [esGestion, pideTarifario]);
  const cerrarTarifario = () => {
    setDialogo(null);
    if (pideTarifario) {
      setParams((p) => { const n = new URLSearchParams(p); n.delete('tarifario'); return n; }, { replace: true });
    }
  };

  const abrirMedico = (m) => { setMedico(m); cargarDetalle(m); };
  const enLista = (resumen || []).filter((m) => m.en_lista && m.activo !== false).map((m) => m.medico_id);
  // Tras tocar la lista o el % fijo: se recarga la entrada y, si hay un
  // colaborador abierto, su tarjeta (su % fijo, si está en la lista).
  const recargarLista = useCallback(() => {
    if (!esGestion) return;
    api.get('/api/colaboradores/resumen')
      .then((r) => {
        const lista = r.data?.medicos || [];
        setResumen(lista);
        setMedico((actual) => (actual ? (lista.find((x) => x.medico_id === actual.medico_id)
          || { ...actual, en_lista: false }) : actual));
      })
      .catch(() => {});
  }, [esGestion]);
  const volver = () => { setMedico(null); setDetalle(null); cargarResumen(); };

  const marcarPagado = async (a) => {
    try {
      await api.post(`/api/colaboradores/actividades/${a.id}/pagado`, null, { params: { pagado: !a.pagado } });
      cargarDetalle(medico);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo actualizar'); }
  };
  const borrar = async (a) => {
    try { await api.delete(`/api/colaboradores/actividades/${a.id}`); cargarDetalle(medico); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo borrar'); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Colaboradores"
        subtitle="Facturación por médico: tratamientos y cirugías, con lo que la clínica le debe a cada uno."
      />

      {esGestion && !medico && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {(resumen || []).map((m) => (
              <div key={m.medico_id} className="flex flex-col" data-testid={`colab-tarjeta-${m.medico_id}`}>
              <button type="button" onClick={() => abrirMedico(m)}
                className="cjo-card p-4 text-left hover:ring-1 hover:ring-[#2E6E5E]/40 transition-[box-shadow] flex-1"
                data-testid={`colab-medico-${m.medico_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#13231E] truncate">{m.medico_nombre || '(sin nombre)'}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {m.tratamientos} tratamientos · {m.cirugias} cirugías
                      {m.ultima_fecha ? ` · última ${m.ultima_fecha}` : ''}
                    </div>
                    {m.en_lista === true && m.activo !== false && (
                      <div className="text-xs text-[#1E4A3F] mt-0.5" data-testid={`colab-resumen-reparto-${m.medico_id}`}>
                        {textoReparto(m)}
                      </div>
                    )}
                    {m.en_lista === false && (
                      <div className="text-xs font-medium text-[#8a5a12] mt-0.5" data-testid={`colab-fuera-lista-${m.medico_id}`}>
                        No está en la lista de colaboradores
                      </div>
                    )}
                    {m.en_lista === true && m.activo === false && (
                      <div className="text-xs font-medium text-[#8a5a12] mt-0.5" data-testid={`colab-de-baja-${m.medico_id}`}>
                        Dado de baja de la lista
                      </div>
                    )}
                    {m.excepciones > 0 && (
                      <div className="text-xs text-[#8a5a12] mt-0.5" data-testid={`colab-resumen-excepciones-${m.medico_id}`}>
                        {textoExcepciones(m)}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pendiente</div>
                    <div className="text-xl font-bold text-[#8a5a12] tabular-nums">{eur(m.pendiente)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ya pagado</div>
                    <div className="text-sm font-medium text-muted-foreground tabular-nums">{eur(m.pagado)}</div>
                  </div>
                </div>
              </button>
              <div className="flex justify-end gap-1 pt-1">
                {m.en_lista === true && m.activo !== false && (
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                    onClick={() => setQuitando(m)} data-testid={`colab-quitar-${m.medico_id}`}>
                    <UserMinus className="w-3.5 h-3.5 mr-1" /> Quitar
                  </Button>
                )}
                {(m.en_lista === false || m.activo === false) && (
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => setAnadiendo({ inicial: m.medico_id })} data-testid={`colab-anadir-${m.medico_id}`}>
                    <UserPlus className="w-3.5 h-3.5 mr-1" /> Añadir a la lista
                  </Button>
                )}
              </div>
              </div>
            ))}
            {resumen && resumen.length === 0 && (
              <Card className="cjo-card p-6 text-sm text-muted-foreground sm:col-span-2 xl:col-span-3"
                data-testid="colab-lista-vacia">
                Aún no hay colaboradores en la lista. Añade el primero con «Añadir colaborador».
              </Card>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setAnadiendo({ inicial: '' })} variant="outline" data-testid="colab-anadir-colaborador">
              <UserPlus className="w-4 h-4 mr-2" /> Añadir colaborador
            </Button>
            <Button onClick={() => setDialogo('tratamiento')} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-nuevo-tratamiento">
              <Syringe className="w-4 h-4 mr-2" /> Añadir tratamiento
            </Button>
            <Button onClick={() => setDialogo('cirugia')} variant="outline" data-testid="colab-nueva-cirugia">
              <Stethoscope className="w-4 h-4 mr-2" /> Añadir cirugía
            </Button>
            <Button onClick={() => setDialogo('tarifario')} variant="ghost" data-testid="colab-tarifario">
              <Tags className="w-4 h-4 mr-2" /> Tarifario
            </Button>
            <Button onClick={() => setDialogo('config')} variant="ghost" data-testid="colab-config">
              <Settings2 className="w-4 h-4 mr-2" /> Porcentajes y fungibles
            </Button>
          </div>
        </>
      )}

      {medico && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {esGestion && (
                <Button variant="ghost" size="sm" onClick={volver} data-testid="colab-volver">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Médicos
                </Button>
              )}
              <h2 className="text-lg font-semibold text-[#13231E]">{medico.medico_nombre}</h2>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setDialogo('tratamiento')} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]">
                <Plus className="w-4 h-4 mr-1" /> Tratamiento
              </Button>
              {/* Cirugía aquí solo GESTIÓN (2026-08-26): este diálogo no lleva
                  paciente asociado y el servidor exige cita de quirófano del
                  paciente ese día para doctores — desde aquí siempre daba
                  error. Otro cirujano registra sus cirugías desde el EPISODIO del
                  día de quirófano, que sí lleva el paciente clavado. */}
              {esGestion && (
                <Button size="sm" variant="outline" onClick={() => setDialogo('cirugia')}>
                  <Plus className="w-4 h-4 mr-1" /> Cirugía
                </Button>
              )}
            </div>
          </div>

          {detalle && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[['Neto total', detalle.totales?.neto], ['Su parte', detalle.totales?.parte_medico],
                ['Parte clínica', detalle.totales?.parte_clinica], ['PENDIENTE de pagarle', detalle.totales?.pendiente]]
                .map(([t, v]) => (
                  <Card key={t} className="cjo-card p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t}</div>
                    <div className={`text-lg font-bold tabular-nums ${t.startsWith('PEND') ? 'text-[#8a5a12]' : 'text-[#13231E]'}`}>{eur(v)}</div>
                  </Card>
                ))}
            </div>
          )}

          <Card className="cjo-card p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground text-left border-b border-border">
                  <th className="py-2 px-3">Fecha</th>
                  <th className="py-2 px-3">Paciente</th>
                  <th className="py-2 px-3">Qué</th>
                  <th className="py-2 px-3 text-right">PVP sin IVA</th>
                  <th className="py-2 px-3 text-right">Costes</th>
                  <th className="py-2 px-3 text-right">Neto</th>
                  <th className="py-2 px-3 text-right">Médico</th>
                  <th className="py-2 px-3 text-right">Clínica</th>
                  <th className="py-2 px-3 text-center">Pagado</th>
                  {esGestion && <th className="py-2 px-1" />}
                </tr>
              </thead>
              <tbody>
                {(detalle?.items || []).map((a) => (
                  <tr key={a.id} className={`border-b border-border/60 ${a.pagado ? 'opacity-60' : ''}`} data-testid={`colab-fila-${a.id}`}>
                    <td className="py-2 px-3 whitespace-nowrap tabular-nums">{a.fecha}</td>
                    <td className="py-2 px-3">{a.paciente_nombre}</td>
                    <td className="py-2 px-3">
                      {a.tipo === 'tratamiento'
                        ? <>{a.tratamiento} <span className="text-xs text-muted-foreground">· {(a.productos?.length ? a.productos.map((l) => `${l.producto}${l.cantidad > 1 ? ` ×${l.cantidad}` : ''}`).join(' + ') : `${a.producto || ''}${a.cantidad > 1 ? ` ×${a.cantidad}` : ''}`)}{a.descuento_pct ? ` · dto ${a.descuento_pct}%` : ''}</span></>
                        : <>{a.intervencion} <span className="text-xs text-muted-foreground">· cirugía{a.lugar_nombre ? ` · ${a.lugar_nombre}` : ''}</span></>}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{eur(a.pvp_sin_iva)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">
                      {a.tipo === 'tratamiento' ? eur(a.coste_producto_total) : eur(a.coste_total)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-medium">{eur(a.neto)}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold text-[#1E4A3F]">
                      {eur(a.parte_medico)}
                      {/* El % cuando no es el general: una excepción (por
                          servicio o por médico) o el % fijo del colaborador. */}
                      {etiquetaPct(a) && !a.revision_sin_tratamiento && (
                        <div className="text-[11px] font-medium text-[#8a5a12] whitespace-nowrap"
                          data-testid={etiquetaPct(a) === '% fijo' ? `colab-pct-fijo-${a.id}` : `colab-excepcion-${a.id}`}>
                          {textoPorcentaje(a.pct_medico)} ({etiquetaPct(a)})
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{eur(a.parte_clinica)}</td>
                    <td className="py-2 px-3 text-center">
                      {esGestion ? (
                        <button type="button" onClick={() => marcarPagado(a)}
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors ${
                            a.pagado ? 'bg-[#2E6E5E] border-[#2E6E5E] text-white' : 'border-border text-transparent hover:border-[#2E6E5E]'}`}
                          title={a.pagado ? `Pagado${a.pagado_por ? ` por ${a.pagado_por}` : ''} — pulsar para deshacer` : 'Marcar como pagado'}
                          data-testid={`colab-pagado-${a.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      ) : (a.pagado ? <Check className="w-4 h-4 text-[#2E6E5E] inline" /> : '—')}
                    </td>
                    {esGestion && (
                      <td className="py-2 px-1">
                        <button type="button" onClick={() => borrar(a)} className="text-muted-foreground hover:text-red-700" title="Borrar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {detalle && detalle.items.length === 0 && (
                  <tr><td colSpan={10} className="py-6 px-3 text-center text-sm text-muted-foreground">Sin actividad todavía.</td></tr>
                )}
              </tbody>
            </table>
          </Card>

          {esGestion && (
            <PorcentajesColaborador key={medico.medico_id} medico={medico} catalogos={catalogos}
              onCambiado={recargarLista}
              onAnadir={() => setAnadiendo({ inicial: medico.medico_id })} />
          )}
          <LiquidacionColaborador key={`liq-${medico.medico_id}`} medicoId={medico.medico_id} esGestion={esGestion} />
        </>
      )}

      {anadiendo && esGestion && (
        <AnadirColaboradorDialog medicos={medicos} enLista={enLista} inicial={anadiendo.inicial}
          anteriores={Object.fromEntries((resumen || []).filter((m) => m.en_lista && m.activo === false)
            .map((m) => [m.medico_id, m.pct_fijo]))}
          onClose={() => setAnadiendo(null)}
          onAnadido={() => { setAnadiendo(null); recargarLista(); }} />
      )}
      {quitando && esGestion && (
        <QuitarColaboradorDialog medico={quitando} onClose={() => setQuitando(null)}
          onQuitado={() => { setQuitando(null); recargarLista(); }} />
      )}

      <ColabActividadDialog
        tipo={dialogo === 'tratamiento' || dialogo === 'cirugia' ? dialogo : null}
        onClose={() => setDialogo(null)}
        catalogos={catalogos}
        medicos={medicos}
        esGestion={esGestion}
        medicoActual={medico}
        onCreada={() => { setDialogo(null); if (medico) cargarDetalle(medico); else cargarResumen(); }}
      />
      {dialogo === 'config' && catalogos && (
        <DialogoConfig config={catalogos.config} medicos={medicos}
          lugares={Array.isArray(catalogos.lugares) ? catalogos.lugares : []}
          onClose={() => setDialogo(null)}
          onGuardada={(cfg) => { setCatalogos((c) => ({ ...c, config: cfg })); }}
          // Una excepción nueva o retirada cambia a quién hay que pedirle el
          // lugar de la cirugía: se recargan los catálogos.
          onExcepciones={cargarCatalogos} />
      )}
      {dialogo === 'tarifario' && esGestion && (
        <TarifarioColaboradoresDialog catalogos={catalogos} estado={estadoCatalogos}
          inicial={pestanaInicial} onClose={cerrarTarifario} onCambiado={cargarCatalogos} />
      )}
    </div>
  );
}

const CAMPOS_GENERAL = [
  ['% del médico en tratamientos', 'pct_medico_tratamiento', true],
  ['% del médico en cirugías', 'pct_medico_cirugia', true],
  ['Fungibles fijos por cirugía (€)', 'fungibles_defecto', false],
];
const paraCampo = (v) => (v === null || v === undefined ? '' : String(v).replace('.', ','));

function DialogoConfig({ config, medicos, lugares, onClose, onGuardada, onExcepciones }) {
  const [f, setF] = useState({
    pct_medico_tratamiento: paraCampo(config?.pct_medico_tratamiento ?? 70),
    pct_medico_cirugia: paraCampo(config?.pct_medico_cirugia ?? 50),
    fungibles_defecto: paraCampo(config?.fungibles_defecto ?? 50),
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const guardar = async () => {
    // «47,5» como en el resto de la pantalla (lib/importeEuros), no Number().
    const cuerpo = {};
    for (const [lbl, k, esPct] of CAMPOS_GENERAL) {
      const n = leerImporte(f[k]);
      if (n === null || Number.isNaN(n)) { setError(`«${lbl}»: escribe solo el número, por ejemplo 45 o 47,5.`); return; }
      if (esPct && n > 100) { setError(`«${lbl}»: tiene que ir de 0 a 100.`); return; }
      cuerpo[k] = n;
    }
    setError(''); setGuardando(true);
    try {
      const r = await api.put('/api/colaboradores/config', cuerpo);
      toast.success('Reparto general guardado');
      onGuardada(r.data);
    } catch (e) { setError(mensajeError(e, 'No se pudo guardar')); }
    finally { setGuardando(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="colab-config-dialogo">
        <DialogHeader>
          <DialogTitle>Porcentajes y fungibles</DialogTitle>
          <DialogDescription>
            El reparto general vale para todos los médicos, salvo las excepciones de abajo. Un cambio vale para los
            actos que se registren a partir de ahora: lo ya registrado conserva su porcentaje.
          </DialogDescription>
        </DialogHeader>
        <section className="space-y-3" data-testid="colab-config-general">
          <h3 className="text-sm font-semibold text-[#13231E]">Reparto general</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {CAMPOS_GENERAL.map(([lbl, k]) => (
              <div key={k}>
                <Label htmlFor={`config-${k}`}>{lbl}</Label>
                <Input id={`config-${k}`} inputMode="decimal" value={f[k]} className="text-right tabular-nums"
                  onChange={(e) => { setError(''); setF((p) => ({ ...p, [k]: e.target.value })); }}
                  data-testid={`config-${k}`} />
              </div>
            ))}
          </div>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="config-error">{error}</p>}
          <div className="flex justify-end">
            <Button onClick={guardar} disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="config-guardar">
              {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar reparto general
            </Button>
          </div>
        </section>
        <div className="border-t border-border pt-4">
          <ExcepcionesReparto medicos={medicos || []} lugares={lugares || []} onCambiado={onExcepciones} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
